import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Refresh, Activity, Users, AlertTriangle } from "@/components/icons";

import { fetchClientVersionStats } from "@/lib/api";
import type { ClientVersionRow } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Stat } from "@/components/Stat";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { formatDuration, formatNumber, formatPercent } from "@voltie/localization-web";
import { useT } from "@/app/localization";

const fmtInt = (n: number) => formatNumber(n, 0);

function platformBadge(platform: string): string {
  switch (platform) {
    case "ios":
      return "bg-info/20 text-info";
    case "android":
      return "bg-success/20 text-success";
    default:
      return "bg-warning/20 text-warning";
  }
}

export function MobileVersionsPage() {
  const t = useT();
  const q = useQuery({
    queryKey: ["mobile", "versions"],
    queryFn: () => fetchClientVersionStats(30),
    refetchInterval: 15_000,
  });

  const data = q.data;
  const rows: ClientVersionRow[] = data?.active_distribution ?? [];
  const totalUsers = rows.reduce((s, r) => s + r.users, 0);
  const platforms = new Set(rows.map((r) => r.platform)).size;
  const collector = data?.collector ?? null;

  // Dropped telemetry means the request buffer overflowed between flushes — the
  // distribution is undercounting. Worth surfacing.
  const dropping = !!collector && collector.dropped > 0;

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("operator.ops.mobile.title")}</h1>
          <div className="text-sm opacity-70">
            {t("operator.ops.mobile.subtitle", { days: data?.active_window_days ?? 30 })}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
          <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
        </Button>
      </div>

      {q.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          {t("operator.ops.mobile.load_failed")}
        </div>
      ) : (
        <>
          {dropping && (
            <div className="flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm text-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {t("operator.ops.mobile.dropping", { count: collector!.dropped })}
              </span>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Stat
              title={t("operator.ops.mobile.active_users")}
              value={fmtInt(totalUsers)}
              icon={<Users className="h-4 w-4 opacity-60" />}
              description={t("operator.ops.mobile.across_platforms", { count: platforms })}
            />
            <Stat title={t("operator.ops.mobile.distinct_versions")} value={fmtInt(rows.length)} />
            <Stat
              title={t("operator.ops.mobile.buffered")}
              value={fmtInt(collector?.buffered ?? 0)}
              description={t("operator.ops.mobile.buffered_hint")}
            />
            <Stat
              title={t("operator.ops.mobile.dropped")}
              value={fmtInt(collector?.dropped ?? 0)}
              description={t("operator.ops.mobile.dropped_hint")}
            />
          </div>

          <Card>
            <CardContent className="space-y-4 py-5">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Activity className="h-4 w-4" /> {t("operator.ops.mobile.distribution")}
              </CardTitle>

              {rows.length === 0 ? (
                <div className="text-sm opacity-60">
                  {t("operator.ops.mobile.empty", { versionHeader: "X-Client-Version", platformHeader: "X-Client-Platform" })}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left opacity-60">
                        <th className="py-2 pr-4 font-medium">{t("operator.ops.mobile.platform")}</th>
                        <th className="py-2 pr-4 font-medium">{t("operator.ops.mobile.version")}</th>
                        <th className="py-2 pr-4 text-right font-medium">{t("operator.ops.mobile.users")}</th>
                        <th className="py-2 font-medium">{t("operator.ops.mobile.share")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const pct = totalUsers ? (r.users / totalUsers) * 100 : 0;
                        return (
                          <tr key={`${r.platform}:${r.app_version}`} className="border-b border-border/50">
                            <td className="py-2 pr-4">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${platformBadge(r.platform)}`}>
                                {r.platform}
                              </span>
                            </td>
                            <td className="py-2 pr-4 font-mono">{r.app_version}</td>
                            <td className="py-2 pr-4 text-right tabular-nums">{fmtInt(r.users)}</td>
                            <td className="py-2">
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                                  <div className="h-full bg-primary/70" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs opacity-70">{formatPercent(pct / 100, 1)}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 border-t border-border pt-4 text-sm sm:grid-cols-4">
                <div>
                  <dt className="opacity-60">{t("operator.ops.mobile.window")}</dt>
                  <dd className="font-mono">{t("operator.ops.mobile.window_days", { days: data?.active_window_days ?? 30 })}</dd>
                </div>
                <div>
                  <dt className="opacity-60">{t("operator.ops.mobile.last_flush_rows")}</dt>
                  <dd className="font-mono">{collector ? fmtInt(collector.last_flush_rows) : "—"}</dd>
                </div>
                <div>
                  <dt className="opacity-60">{t("operator.ops.mobile.flushes")}</dt>
                  <dd className="font-mono">
                    {collector ? `${fmtInt(collector.flushes_ok)} / ${fmtInt(collector.flushes_failed)}` : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="opacity-60">{t("operator.ops.auto_refresh")}</dt>
                  <dd className="font-mono">{formatDuration(15, "seconds")}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
